/**
 * UBB-372 — <plp-buy-form>: buy form on chrome-less PLP/landing pages
 * (snippets/plp-buy-form.liquid). Routes the submit:
 *   - "One time" selected (or no subscription UI): do nothing — the native form POST to
 *     /cart/add carries return_to=/checkout, so Shopify redirects straight to checkout.
 *   - "Subscribe" selected: navigate to the Loop direct-to-checkout URL held by the
 *     selected frequency <option> (from the variant's spec.loop_N_month_subscription_url).
 * Also mirrors the Loop widget's card states: toggles the selected card styling, disables
 * the frequency <select> while "One time" is active, and keeps the Subscribe card's
 * price + savings badge in sync with the chosen frequency (option data-price /
 * data-save-percent, emitted server-side from selling_plan_allocations).
 * On connect it also replaces the server-rendered fallback benefits band with the
 * merchant-set copy from a "Loop Subscribe Custom" block's [data-loop-subscribe-benefits]
 * JSON node when the section has one — the same precedence the real widget uses
 * (loop-widget.js resolveBenefits), so PLP/landing pages stay in line with the PDP.
 */
class PlpBuyForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.frequencySelect = this.querySelector('.plp-buy__frequency');
    this.subPrice = this.querySelector('[data-plp-sub-price]');
    this.subBadge = this.querySelector('[data-plp-sub-badge]');
    this.applyMerchantBenefits();
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
    this.frequencySelect?.addEventListener('change', () =>
      this.syncSubscribePrice()
    );
    this.querySelectorAll('.plp-buy__radio').forEach((radio) => {
      radio.addEventListener('change', () => this.syncCardState());
    });
    this.syncCardState();
    this.syncSubscribePrice();
  }

  /** @returns {boolean} true when the "Subscribe" order type is selected */
  isSubscribeSelected() {
    return this.querySelector('.plp-buy__radio:checked')?.value === 'subscribe';
  }

  /** Mirrors radio state onto the Loop-styled cards and the frequency dropdown. */
  syncCardState() {
    this.querySelectorAll('.plp-buy__card').forEach((card) => {
      const checked = card.querySelector('.plp-buy__radio')?.checked;
      card.classList.toggle(
        'loop-widget-purchase-option-selected',
        Boolean(checked)
      );
    });
    if (this.frequencySelect)
      this.frequencySelect.disabled = !this.isSubscribeSelected();
  }

  /** Updates the Subscribe card's price and savings badge from the selected frequency option. */
  syncSubscribePrice() {
    const option = this.frequencySelect?.selectedOptions[0];
    if (!option) return;
    if (this.subPrice) this.subPrice.textContent = option.dataset.price || '';
    if (this.subBadge) {
      const percent = option.dataset.savePercent;
      // Same badge format the brand layer writes on the PDP widget (loop-widget.js).
      this.subBadge.textContent = percent ? `Save ${percent}% TOTAL` : '';
      this.subBadge.hidden = !percent;
    }
  }

  /**
   * Replaces the fallback benefits band with the merchant-set benefits from the section's
   * "Loop Subscribe Custom" block node, when present. Built with createElement/textContent
   * (not innerHTML) because merchant-entered text flows here.
   */
  applyMerchantBenefits() {
    const band = this.querySelector('.loop-subscribe-benefits');
    const node =
      this.closest('.shopify-section')?.querySelector(
        '[data-loop-subscribe-benefits]'
      ) || document.querySelector('[data-loop-subscribe-benefits]');
    if (!band || !node) return;
    let benefits;
    try {
      benefits = JSON.parse(node.textContent).benefits;
    } catch {
      return;
    }
    if (!Array.isArray(benefits) || benefits.length === 0) return;
    band.textContent = '';
    benefits.forEach((benefit) => {
      const row = document.createElement('div');
      row.className = 'loop-subscribe-benefit';
      const icon = document.createElement('img');
      icon.className = 'loop-subscribe-benefit-icon';
      icon.src = benefit.icon;
      icon.alt = '';
      icon.width = 19;
      icon.height = 19;
      icon.loading = 'lazy';
      const text = document.createElement('span');
      text.className = 'loop-subscribe-benefit-text';
      text.textContent = benefit.text;
      row.append(icon, text);
      band.appendChild(row);
    });
  }

  /**
   * Sends subscription orders to the Loop checkout URL; lets one-time orders
   * submit natively (POST /cart/add + return_to=/checkout).
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.isSubscribeSelected()) return;
    event.preventDefault();
    const loopUrl = this.frequencySelect?.value;
    if (loopUrl) window.location.href = loopUrl;
  }
}

customElements.define('plp-buy-form', PlpBuyForm);
